import React, { useEffect, useState } from "react";
import ScrollToTop from "react-scroll-to-top";
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Button,
  Container,
  Grid,
  Typography,
  Box,
  Stack,
  CardHeader,
} from "@mui/material";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import makeStyles from "@material-ui/styles/makeStyles";
import VisibilityIcon from '@mui/icons-material/Visibility';

import { getPerkData, deletePerk } from "../../redux/actions/perk";
import CreatePerk from "../../components/layout/CreatePerk";
import PerkStatus from "../../components/layout/PerkStatus";
import WelcomePerkModal from "../../components/layout/WelcomePerkModal";
import ConfirmDeletePerk from "./ConfirmDeletePerk";

import config from "../../config/config";
import "./PerkPage.style.css";
import ViewPerk from "./ViewPerk";


const PerksAdmin = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const [showCreatePerk, setShowCreatePerk] = useState(false);
  const [showDeletePerk, setShowDeletePark] = useState(false);
  const [showViewPerk, setShowViewPerk] = useState(false);
  const [selectedPerk, setSelectedPerk] = useState();
  const [showWelcomePerkModal, setShowWelcomePerkModal] = useState(false);

  useEffect(() => {
    onInit();
  }, []);

  const onInit = async () => {
    dispatch(getPerkData());
  }

  const useStyles = makeStyles({
    title: {
      color: theme.palette.mode === 'light' ? '#b5b5c3' : '#474761',
      fontSize: '13px',
      fontWeight: '500',
      lineHeight: '18px',
      '&:hover': {
        color: '#009ef7 !important',
      }
    },
    subheader: {
      color: theme.palette.mode === 'light' ? '#3f4254' : '#cdcdde',
      fontSize: '12.35px',
      fontWeight: '600',
      lineHeight: '18px',
    }
  });
  const styles = useStyles();

  const { loading, perkData, user } = useSelector(state => ({
    loading: state.book.loading,
    user: state.auth.user,
    perkData: state.perk.perkData
  }));

  useEffect(() => {
    if (user?.id) {
      const dontShow = localStorage.getItem(user.id + "_DONT_SHOW_WELCOME_PERK_TIP") || "false";
      if (dontShow === 'false') {
        setShowWelcomePerkModal(true);
      }
    }
  }, [user]);
  
  function getViewCountText(count) {
    return count + (count > 1 ? ' views' : ' view');
  }

  function getDaysRemainingText(expiryDate) {
    const daysRemaining = moment(expiryDate).diff(moment(), 'days') + 1;
    return moment(expiryDate).isBefore(moment())
      ? 'Expired'
      : daysRemaining + ' day' + (daysRemaining > 1 ? 's ' : ' ') + 'left';
  }
  
  const onOpenCreatePerk = () => setShowCreatePerk(true);
  const onCloseCreatePerk = () => setShowCreatePerk(false);
  const onCloseWelcomePerkModal = () => setShowWelcomePerkModal(false);
  
  const onOpenDeletePerk = (perk) => {
    setShowDeletePark(true);
    setSelectedPerk(perk);
  }
  const onCloseDeletePerk = () => setShowDeletePark(false);

  const onOpenViewPerk = () => setShowViewPerk(true);
  const onCloseViewPerk = () => setShowViewPerk(false);

  const onConfirmDeletePerk = () => {
    const data = { id: selectedPerk._id };
    dispatch(deletePerk(data));
    onCloseDeletePerk();
    setSelectedPerk(null);
  }

  const onEditPerk = (perk) => {
    setSelectedPerk(perk);
    onOpenCreatePerk();
  }

  const onViewPerk = (perk) => {
    setSelectedPerk(perk);
    onOpenViewPerk();
  }

  return (
    <Container maxWidth="lg">
      <Grid
        item
        className="page-title"
        container
        justifyContent='space-between'
        sx={{ my: { sm: 2, md: 5 }, marginInline: 'auto' }}
      >
        <Grid item xs={12} sm={6}>
          <Typography sx={{ color: '#fff', fontSize: '1.5rem', fontWeight: 600 }}>My Loyalty Perks</Typography>
          <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 500, mt: '3px', opacity: 0.75 }}>
            Create exclusive rewards, incentives and curated experiences to encourage repeat business and attract new clients.
          </Typography>
        </Grid>
        <Grid item container direction='row' justifyContent='flex-end' alignItems="center" xs={12} sm={6}>
          <Button
            onClick={() => {
              onOpenCreatePerk();
              setSelectedPerk(null);
            }}
            color="primary"
            variant="contained"
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              color: '#fff',
              padding: '11px 20px',
              borderRadius: '6px',
            }}>
            Create New Perk
          </Button>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        sx={{ mt: 2 }}
      >
        {perkData.length === 0 && (
          <Grid
            item
            container
            display='flex'
            direction='row'
            alignItems="center"
            justifyContent='center'
            sx={{
              minHeight: '500px',
              backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d',
              borderRadius: 2
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: theme.palette.mode ==="light" ? "#3f4254" : "#fff" }}>You have no active Perks</Typography>
          </Grid>
        )}
        {perkData.map((perk, index) => (
          <Grid
            key={index}
            item
            container
            md={12}
            lg={4}
            xs={12}
          >
            <Container sx={{ backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d', padding: '0px !important', borderRadius: '8px' }}>
              <Box className="overlay" onClick={() => onViewPerk(perk)}>
                <Box
                  sx={{
                    backgroundImage: `url(${config.SERVER_BASE_URL}/${perk.featuredImage})`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    backgroundPosition: 'center',
                    minHeight: '250px',
                    borderRadius: '8px'
                  }}
                />
                <Box component="div" className="overlay-layer">
                  <VisibilityIcon sx={{ color: '#fff' }} />
                </Box>
              </Box>
              <Box sx={{ padding: '30px' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <CardHeader
                    classes={{ title: styles.title, subheader: styles.subheader }}
                    avatar={user == null || user?.avatar?.includes("https://gravatar.com/") ?
                      <Avatar sx={{ bgcolor: 'tomato', width: '35px', height: '35px' }} alt="User Name" variant='rounded'>
                        {user?.firstName[0]}
                      </Avatar> :
                      <Box
                        component="img"
                        src={`${config.SERVER_BASE_URL}/${user.avatar}`}
                        alt="avatar"
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                        }}
                      />
                    }
                    title="Requirement"
                    subheader={perk.requirement}
                    sx={{ padding: 0 }}
                  />
                  <PerkStatus status={perk.status} />
                </Stack>
                <Typography onClick={() => onViewPerk(perk)} sx={{ color: theme.palette.mode === 'light' ? '#181c32' : '#fff', fontSize: '28px', fontWeight: 700, lineHeight: '33px', cursor: 'pointer', mt: 2 }}>
                  {perk.title}
                </Typography>
                <Typography sx={{ color: theme.palette.mode === 'light' ? '#181c32' : '#fff', fontSize: '14px', mt: 1 }}>{getDaysRemainingText(perk.expiryDate)} • {perk.location} • {getViewCountText(perk.visitCount)}</Typography>
                <Typography sx={{ color: theme.palette.mode === 'light' ? '#5e6278' : '#92929f', fontSize: '14px', fontWeight: 500, mt: 2 }}>
                  {perk.summary}
                </Typography>
                <Stack direction="row" sx={{ mt: 2 }}>
                  <Button
                    onClick={() => onEditPerk(perk)}
                    color="primary"
                    variant="contained"
                    sx={{
                      width: '150px',
                      fontSize: '14px',
                      fontWeight: 600,
                      textTransform: 'none',
                      color: '#fff',
                      padding: '11px 20px',
                      borderRadius: '6px'
                    }}
                  >
                    Edit Perk
                  </Button>
                  <Button
                    onClick={() => onOpenDeletePerk(perk)}
                    color="error"
                    variant="contained"
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      textTransform: 'none',
                      color: '#fff',
                      padding: '11px 20px',
                      borderRadius: '6px',
                      ml: 1,
                    }}
                  >
                    Delete
                  </Button>
                </Stack>
              </Box>
            </Container>
          </Grid>
        ))}
      </Grid>
      <CreatePerk
        visible={showCreatePerk}
        onClose={onCloseCreatePerk}
        perk={selectedPerk}
      />
      <ConfirmDeletePerk
        visible={showDeletePerk}
        onClose={onCloseDeletePerk}
        onConfirm={onConfirmDeletePerk}
      />
      <ViewPerk
        visible={showViewPerk}
        onClose={onCloseViewPerk}
        perk={selectedPerk}
      />
      <WelcomePerkModal
        visible={showWelcomePerkModal}
        onClose={onCloseWelcomePerkModal}
        id={user?.id}
      />
      <ScrollToTop
        smooth
        color="#009EF7"
      />
    </Container>
  );
}

export default PerksAdmin;
